import { useEffect, useState } from "react";

import { Box, Button, Divider, FormControl, FormErrorMessage, FormLabel, HStack, Icon, Input, Switch, Text, Textarea, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { FaEye } from "react-icons/fa";

import EditIcon from "@/assets/EditIcon";
import { COLORS, DURATION_TOAST, REGISTRATION_STATUS } from "@/common/constants";
import FileUploader from "@/components/Fields/FileUploader";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCountry from "@/components/Selects/SelectCountry";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { shipOwnerSchema } from "@/schemas/shipOwnerSchema";
import { downloadFile } from "@/services/downloadService";
import { getShipOwnerById, postShipOwner, putShipOwner } from "@/services/shipOwnerService";

const EditShipOwner = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);
    const toast = useToast();

    const { id, returnPath } = location.state || {};

    const [dataDetail, setDataDetail] = useState<any>(null);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [editableDocs, setEditableDocs] = useState<string[]>([]);

    const navigateBack = () => {
        navigate(returnPath || '/manage-ship-operator');
    }

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes to this ship operator?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Save Ship Operator?");
        setConfirmationTest("Are you sure you want to save this operator?");
        setConfirmButtonText("Save Operator");
        setCancelButtonText("Cancel");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataAdd = () => {
        const data = new FormData();
        data.append('OnBehalfCompany', dataEdit.onBehalfCompany);
        data.append('Address', dataEdit.address.trim());
        data.append('Country', dataEdit.country);
        data.append('ContactPerson', dataEdit.contactPerson);
        data.append('Email', dataEdit.email);
        data.append('AlternateEmail', dataEdit.alternateEmail);
        data.append('Documentfile', dataEdit.doc || '');
        data.append('RegisterStatus', String(REGISTRATION_STATUS.ACTIVE));
        return data;
    }

    const generateDataEdit = () => {
        const data = new FormData();
        data.append('ShipOwnerId', id);
        data.append('OnBehalfCompany', dataEdit.onBehalfCompany);
        data.append('Address', dataEdit.address.trim());
        data.append('Country', dataEdit.country);
        data.append('ContactPerson', dataEdit.contactPerson);
        data.append('Email', dataEdit.email);
        data.append('AlternateEmail', dataEdit.alternateEmail);
        data.append('Phone', dataEdit.phone);
        data.append('RegisterStatus', String(dataEdit.status ? REGISTRATION_STATUS.ACTIVE : REGISTRATION_STATUS.INACTIVE));
        data.append('Documentfile', dataEdit.doc || '');
        data.append('ModifiedBy', name);
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title.startsWith("Cancel")) {
            navigateBack();
            return;
        }
        
        let obj: FormData;
        let response: any;

        if (id){
            obj = generateDataEdit();
            response = await putShipOwner(obj);
        } else {
            obj = generateDataAdd();
            response = await postShipOwner(obj);
        }
        
        if (response.status === HttpStatusCode.Ok) {
            setIsSubmitting(false);
            toast({
                title: 'Ship operator saved.',
                description: "We've saved the ship operator for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setTimeout(() => {
                navigateBack();
            }, 1000)
        } else {
            setIsSubmitting(false);
            toast({
                title: 'Ship operator saving failed.',
                description: "We could not save the ship operator for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleShowEditImage = (name: string) => {
        setEditableDocs((prevDocs) => {
            if (prevDocs.includes(name)) {
                return prevDocs.filter((doc) => doc !== name);
            }

            return [...prevDocs, name];
        });
    }

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const getDataDetail = async () => {
        const response = await getShipOwnerById(id) as any;
        if (response) {
            setDataDetail(response.data);
        }
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        onBehalfCompany: dataDetail?.onBehalfCompany ?? '',
                        address: dataDetail?.address ?? '',
                        country: dataDetail?.country ?? '',
                        contactPerson: dataDetail?.contactPerson ?? '',
                        email: dataDetail?.email ?? '',
                        alternateEmail: dataDetail?.alternateEmail ?? '',
                        phone: dataDetail?.phone ?? '',
                        status: dataDetail?.status
                    }}
                    validationSchema={shipOwnerSchema}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='onBehalfCompany'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.onBehalfCompany && form.touched.onBehalfCompany} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">On Behalf Company<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='On Behalf Company' className="form-input" />
                                                <FormErrorMessage>{form.errors.onBehalfCompany}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='address'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.address && form.touched.address} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Address<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Textarea {...field} placeholder='Address' className="form-input" />
                                                <FormErrorMessage>{form.errors.address}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='country'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.country && form.touched.country} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} defaultValue={props.values.country} />
                                                <FormErrorMessage>{form.errors.country}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='contactPerson'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.contactPerson && form.touched.contactPerson} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Contact Person<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Contact Person' className="form-input" />
                                                <FormErrorMessage>{form.errors.contactPerson}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='email'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.email && form.touched.email} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Email' className="form-input" />
                                                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='alternateEmail'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.alternateEmail && form.touched.alternateEmail} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Alternative Email</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Alternative Email' className="form-input" />
                                                <FormErrorMessage>{form.errors.alternateEmail}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='phone'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.phone && form.touched.phone} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Phone Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Phone Number' className="form-input" />
                                                <FormErrorMessage>{form.errors.phone}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='doc'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.doc && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">DOC</FormLabel>
                                            <div className="form-input-container">
                                                {dataDetail?.documents && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={4}
                                                        backgroundColor="green.50"
                                                        borderColor="green.200"
                                                        width="100%"
                                                        marginBottom="15px"
                                                    >
                                                        <HStack
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={3}
                                                            backgroundColor="white"
                                                            justifyContent="space-between"
                                                            width="100%"
                                                        >
                                                            <HStack>
                                                                <Text>DOC File</Text>
                                                            </HStack>
                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.documents)}>
                                                                <Icon as={FaEye} />
                                                                <Text>Download Document</Text>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>
                                                    <EditIcon
                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                        onClick={() => handleShowEditImage('doc')}
                                                    />
                                                </div>}

                                                {(!dataDetail?.documents || editableDocs.includes('doc')) &&
                                                    <div style={{ width: '95%' }}>
                                                        <FileUploader
                                                            onDrop={(e) => props.setFieldValue('doc', e[0])}
                                                            isInvalid={form.errors.NIB && form.submitCount}
                                                            type="registration-inspectingcompany"
                                                            isDisabled={props.isSubmitting}
                                                            isPadding={false}
                                                        />
                                                    </div>
                                                }
                                                <FormErrorMessage>{form.errors.doc}</FormErrorMessage>
                                            </div>
                                        </div>
                                        
                                    </FormControl>
                                )}
                            </Field>
                            {Boolean(id) && <Field name='status'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Status
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={props.values.status} onChange={() => props.setFieldValue('status', !props.values.status)} id='status-switch' className="form-input" />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                >
                                    Save Ship Operator
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default EditShipOwner;
